import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TreeModule } from 'primeng/tree';
import { BehaviorSubject, combineLatest, map, Observable, of, shareReplay } from 'rxjs';
import { DestinationFilter, JuniorPriceList, JuniorPriceLists } from '../../models';
import { CompanyEnum } from '../../models/company.model';
import { ApplicationFormService } from '../../services/application-form.service';
import { FilterService } from '../../services/filter.service';
import { SharedDataStore } from '../../store';
import { JuniorCampsPriceListDataComponent } from '../junior-camps-pricelist-data/junior-camps-price-list-data.component';
import { PriceListPanelComponent } from '../price-list-panel/price-list-panel.component';

@Component({
  standalone: true,
  selector: 'lx-price-lists-portal-junior-camps',
  imports: [CommonModule, ProgressSpinnerModule, TreeModule, TableModule, PriceListPanelComponent, JuniorCampsPriceListDataComponent],
  templateUrl: './junior-camps.component.html',
  styleUrls: ['./junior-camps.component.scss'],
})
export class JuniorCampsComponent implements OnInit {
  // Junior camps are only available for ALP so the style will be the one for ALP always
  company = CompanyEnum.ALP;

  juniorCamps$: Observable<JuniorPriceLists> | undefined;
  filters$: Observable<DestinationFilter[]> | undefined;

  applicationForm = [];

  private filtersSubject = new BehaviorSubject<DestinationFilter[]>([]);
  private selectedFilters$ = this.filtersSubject.asObservable();

  private readonly applicationFormService = inject(ApplicationFormService);
  private readonly filterService = inject(FilterService);
  private readonly sharedDataStore = inject(SharedDataStore);

  ngOnInit(): void {
    const juniorCamps$ = this.sharedDataStore.juniorCampsPriceLists$.pipe(shareReplay(1));

    this.filters$ = juniorCamps$.pipe(map((juniorCamps) => this.filterService.getJuniorCampDestinationFilters(juniorCamps) || []));

    this.initializeFilteredCamps(juniorCamps$);

    this.applicationForm = [{ ...this.applicationFormService.getSummerCampApplicationForm(this.company) }];
  }

  filterJuniorCamps(filters: DestinationFilter | DestinationFilter[]): void {
    this.filtersSubject.next(Array.isArray(filters) ? filters : [filters]);
  }

  /**
   * Initializes the observable for filtered junior camps based on the selected filters.
   * @param juniorCamps$ the observable containing the junior camps data from API
   */
  private initializeFilteredCamps(juniorCamps$: Observable<JuniorPriceLists>) {
    this.juniorCamps$ = combineLatest([juniorCamps$ || of({ camps: [] }), this.selectedFilters$]).pipe(
      map(([juniorCamps, selectedFilters]) => {
        if (!juniorCamps || !juniorCamps.camps) {
          return { camps: [] };
        }

        if (!selectedFilters?.length) {
          return { ...juniorCamps };
        }

        const filteredCamps = juniorCamps.camps.filter(
          ({
            juniorCampInfo: {
              country: { code: countryCode },
              school: { code: schoolCode },
            },
          }: JuniorPriceList) =>
            (countryCode && selectedFilters.some((filter) => countryCode === filter.key)) ||
            (schoolCode && selectedFilters.some((filter) => schoolCode === filter.key)),
        );

        return {
          camps: filteredCamps,
        };
      }),
    );
  }
}
